/**
 * Project Analytics on YOM (managed-org strategy). Owner-first:
 *   value cards → traffic over time + right now → where visitors came from +
 *   top pages → shop (if the site sells) → more details (devices, countries).
 * Before the first visit it shows the first-run state instead.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ChevronDown } from 'lucide-react';
import { analyticsEmbedHelpers, api, apiHelpers } from '@/lib/api';
import { fetchYomReport, type OverviewData } from '@/lib/yom/reportClient';
import { qk } from '@/lib/queryKeys';
import { cn } from '@/lib/utils';
import { intervalFor, localDate } from '@/lib/yom/range';
import { cardsForPlugins } from '@/lib/yom/valueCards';
import type { ProjectAnalyticsStatus } from '@/lib/yom/types';
import type { Project, ProjectPlugin } from '@/types';
import { useEmbedTokens } from './useEmbedTokens';
import { YomReport } from './YomReport';
import { ValueCards, useValueSnapshot } from './ValueCards';
import { LiveNowCard, LiveNowPill, useRealtime } from './LiveNow';
import { RangeControls, useRangeState } from './RangeControls';
import { OpenFullAnalyticsButton } from './OpenFullAnalyticsButton';
import { FirstRunState } from './FirstRunState';
import { DataState } from '@/components/common/DataState';
import { DashboardSkeleton } from './ReportSkeleton';

function siteUrlFor(
  status: ProjectAnalyticsStatus,
  project: Pick<Project, 'domains' | 'domain' | 'sitesUrl'> | undefined
): string | null {
  if (status.siteUrl) return status.siteUrl;
  const domain = project?.domains?.[0] || project?.domain;
  if (domain) return /^https?:\/\//.test(domain) ? domain : `https://${domain}`;
  return project?.sitesUrl ?? null;
}

export function ProjectYomAnalytics({
  projectId,
  status,
}: {
  projectId: string;
  status: ProjectAnalyticsStatus;
}) {
  const qc = useQueryClient();
  const { state, update } = useRangeState('7d');
  const { range, compare } = state;
  const [moreOpen, setMoreOpen] = useState(false);

  const { manager, first } = useEmbedTokens(`project:${projectId}`, () =>
    analyticsEmbedHelpers.getEmbedToken(projectId)
  );

  const projectQ = useQuery({
    queryKey: qk.project(projectId),
    queryFn: () => apiHelpers.getProject(projectId),
  });
  const pluginsQ = useQuery({
    queryKey: qk.projectPlugins(projectId),
    queryFn: () => apiHelpers.getProjectPlugins(projectId),
  });
  const project = projectQ.data?.data?.data;
  const installed = useMemo(() => {
    const list: Partial<ProjectPlugin>[] = pluginsQ.data?.data?.data ?? [];
    return new Set<string>(
      list
        .filter((p) => p?.isEnabled !== false)
        .map((p) => p?.pluginId ?? p?.plugin?.slug ?? p?.plugin?.id)
        .filter((id): id is string => Boolean(id))
    );
  }, [pluginsQ.data]);
  const cards = cardsForPlugins(installed);
  const hasShop = installed.has('ecommerce');
  const shopQ = useQuery({
    queryKey: qk.commerceSettings(projectId),
    queryFn: async () => {
      const res = await api.get(`/projects/${projectId}/ecommerce/settings`);
      return res.data?.data as { currency?: string } | undefined;
    },
    enabled: hasShop && !status.currency,
  });
  const currency = status.currency || shopQ.data?.currency || 'USD';

  const snapshot = useValueSnapshot(projectId, manager, range, compare);
  const zeroInRange =
    snapshot.data !== undefined &&
    snapshot.data.current.visitors === 0 &&
    snapshot.data.current.pageviews === 0;

  // Nothing in the selected dates. Has the site EVER had a visit? Only asked
  // when needed (one overview over everything since collection started), so a
  // quiet week on an established site never looks like day one.
  const everQ = useQuery({
    queryKey: qk.analyticsEver(projectId),
    queryFn: async ({ signal }) => {
      const b = await manager!.getBundle();
      const today = new Date();
      const yearAgo = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
      const since = status.collectingSince ? new Date(status.collectingSince) : yearAgo;
      const from = since > yearAgo ? since : yearAgo;
      const data = await fetchYomReport<OverviewData>(
        b.sources.site,
        'overview',
        { from: localDate(from), to: localDate(today), compare: 'false' },
        (rejected) => manager!.getToken('site', { rejected }),
        signal
      );
      return (data.kpis.visitors ?? 0) > 0 || (data.kpis.pageviews ?? 0) > 0;
    },
    enabled: Boolean(manager) && !status.firstEventAt && zeroInRange,
    staleTime: 5 * 60_000,
  });
  const firstRun = !status.firstEventAt && zeroInRange && everQ.data === false;

  // Wait for the numbers before choosing between the first-run state and the
  // dashboard, so a brand-new site never flashes an empty dashboard.
  const deciding = !status.firstEventAt && (snapshot.isPending || (zeroInRange && everQ.isPending));

  // Poll faster while we wait for the very first visitor.
  const realtime = useRealtime(projectId, manager, firstRun ? 10_000 : 30_000);
  const liveCount = realtime.data?.active_visitors ?? 0;
  useEffect(() => {
    if (firstRun && liveCount > 0) {
      void qc.invalidateQueries({ queryKey: qk.analyticsEver(projectId) });
      void qc.invalidateQueries({ queryKey: ['analytics', projectId, 'values'] });
    }
  }, [firstRun, liveCount, qc, projectId]);

  const siteUrl = siteUrlFor(status, project);
  const name = project?.name;

  return (
    <div className="space-y-5 sm:space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <Link
            to={`/projects/${projectId}`}
            className="hover:bg-muted -ml-2 mt-0.5 rounded-lg p-2 transition-colors"
            aria-label="Back to project"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight">Analytics</h1>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
              {name && <span className="text-muted-foreground truncate text-sm">{name}</span>}
              {!firstRun && <LiveNowPill data={realtime.data} />}
            </div>
          </div>
        </div>
        <div className="flex shrink-0 gap-2 sm:pt-1">
          <OpenFullAnalyticsButton projectId={projectId} status={status} />
        </div>
      </div>

      <DataState query={first} hideRefreshingHint skeleton={<DashboardSkeleton />}>
        {() =>
          deciding ? (
            <DashboardSkeleton />
          ) : manager && firstRun ? (
            <FirstRunState
              collectingSince={status.collectingSince}
              siteUrl={siteUrl}
              live={realtime.data}
            />
          ) : manager ? (
            <div className="space-y-5 sm:space-y-6">
              <RangeControls state={state} update={update} />
              <ValueCards
                projectId={projectId}
                manager={manager}
                range={range}
                compare={compare}
                cards={cards}
                currency={currency}
              />

              <div className="grid gap-5 lg:grid-cols-3">
                <YomReport
                  manager={manager}
                  source="site"
                  report="timeseries"
                  title="Visitors over time"
                  metric="visitors"
                  interval={intervalFor(range)}
                  range={range}
                  skeleton="chart"
                  className="lg:col-span-2"
                />
                <LiveNowCard data={realtime.data} isPending={realtime.isPending} />
              </div>

              <div className="grid gap-5 lg:grid-cols-2">
                <YomReport
                  manager={manager}
                  source="site"
                  report="breakdown"
                  title="Where visitors came from"
                  dim="channel"
                  limit={8}
                  range={range}
                  skeleton="table"
                />
                <YomReport
                  manager={manager}
                  source="site"
                  report="breakdown"
                  title="Most visited pages"
                  dim="path"
                  limit={8}
                  range={range}
                  skeleton="table"
                />
              </div>

              {hasShop && (
                <YomReport
                  manager={manager}
                  source="site"
                  report="ecommerce"
                  title="Your shop"
                  currency={currency}
                  range={range}
                  skeleton="shop"
                />
              )}

              <section>
                <button
                  type="button"
                  onClick={() => setMoreOpen((o) => !o)}
                  aria-expanded={moreOpen}
                  className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-sm font-medium"
                >
                  <ChevronDown
                    className={cn('h-4 w-4 transition-transform', moreOpen && 'rotate-180')}
                    aria-hidden
                  />
                  More details
                </button>
                {moreOpen && (
                  <div className="mt-4 grid gap-5 lg:grid-cols-2">
                    <YomReport
                      manager={manager}
                      source="site"
                      report="breakdown"
                      title="Devices"
                      dim="device"
                      limit={5}
                      range={range}
                      skeleton="table"
                    />
                    <YomReport
                      manager={manager}
                      source="site"
                      report="breakdown"
                      title="Countries"
                      dim="country"
                      limit={8}
                      range={range}
                      skeleton="table"
                    />
                    <YomReport
                      manager={manager}
                      source="site"
                      report="breakdown"
                      title="Websites that sent visitors"
                      dim="referrer"
                      limit={8}
                      range={range}
                      skeleton="table"
                    />
                    <YomReport
                      manager={manager}
                      source="site"
                      report="breakdown"
                      title="Campaigns"
                      dim="utm_campaign"
                      limit={8}
                      range={range}
                      skeleton="table"
                    />
                  </div>
                )}
              </section>
            </div>
          ) : null
        }
      </DataState>

      <p className="text-muted-foreground text-center text-xs">Analytics by YOM</p>
    </div>
  );
}
